import { useId } from 'react';

interface MetricCardProps {
  label: string;
  value: number;
  /** What the count is, shown under the number. */
  caption: string;
  /** The same count over the previous period; drives the trend arrow. */
  comparison?: { previous: number; period: string };
}

export default function MetricCard({ label, value, caption, comparison }: MetricCardProps) {
  const labelId = useId();
  const trend =
    !comparison || value === comparison.previous ? null : value > comparison.previous ? 'up' : 'down';

  return (
    // 19px plus the card's 1px border: the design measures 20 from the outer edge.
    <div role="group" aria-labelledby={labelId} className="card p-[19px]">
      <p id={labelId} className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <div className="mt-2 flex items-center gap-2">
        <span className="text-[32px] font-bold leading-[48px] text-gray-800 dark:text-gray-100">
          {value.toLocaleString('en-US')}
        </span>
        {trend && comparison && (
          <span
            role="img"
            aria-label={`Trending ${trend} (compared to ${comparison.period})`}
            className={trend === 'up' ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}
          >
            <TrendArrow direction={trend} />
          </span>
        )}
      </div>
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{caption}</p>
    </div>
  );
}

/** The design's arrow: a diagonal with a right-angle head about two thirds of its length, which
 *  none of lucide's arrows draws. */
function TrendArrow({ direction }: { direction: 'up' | 'down' }) {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path d={direction === 'up' ? 'M4 20 20 4M9 4h11v11' : 'M4 4l16 16M20 9v11H9'} />
    </svg>
  );
}
